import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formSchemasApi } from '../api';
import { queryKeys } from '../queries/keys';
import { FormSchemaDefinition, FormKind } from '../types/formSchema';

export function useFormSchemas() {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: queryKeys.formSchemas(),
    queryFn: () => formSchemasApi.list(),
  });

  const saveMutation = useMutation({
    mutationFn: ({ formKind, schema }: { formKind: string; schema: FormSchemaDefinition }) =>
      formSchemasApi.save(formKind, schema),
    onSuccess: (saved, { formKind }) => {
      queryClient.setQueryData<FormSchemaDefinition[]>(queryKeys.formSchemas(), (prev) =>
        (prev ?? []).map((schema) => (schema.formKind === formKind ? saved : schema)),
      );
    },
  });

  const fetchAll = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKeys.formSchemas() });
  };

  const saveSchema = async (formKind: string, schema: FormSchemaDefinition) => {
    try {
      return await saveMutation.mutateAsync({ formKind, schema });
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      const msg = err?.response?.data?.detail || err?.message || 'Failed to save';
      throw new Error(msg);
    }
  };

  return {
    schemas: query.data ?? [],
    loading: query.isPending,
    error: query.error
      ? ((query.error as { message?: string })?.message || 'Failed to load form schemas')
      : saveMutation.error
        ? ((saveMutation.error as { message?: string })?.message || 'Failed to save')
        : null,
    saving: saveMutation.isPending,
    fetchAll,
    saveSchema,
  };
}

/**
 * Lightweight hook for consuming a single form schema (used by PnidMarkDialog).
 * Returns null while loading; falls back to null so the caller can render hardcoded fields.
 */
export function useFormSchema(kind: FormKind | string | null): FormSchemaDefinition | null {
  const query = useQuery({
    queryKey: queryKeys.formSchemas(),
    queryFn: () => formSchemasApi.list(),
    select: (schemas) => (kind ? schemas.find((schema) => schema.formKind === kind) ?? null : null),
    enabled: Boolean(kind),
  });

  return query.data ?? null;
}
